// Scoped backend access with session authentication and request activity.
export const API_URL = typeof import.meta.env.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL : 'http://localhost:8080';

/** Callers declare the backend response contract; untyped JSON stays unknown. */
export interface ApiResponse extends Omit<Response, 'json'> { json<T = unknown>(): Promise<T> }

export type ApiActivity = 'blocking' | 'background' | 'silent';

export interface ApiFetchOptions {
  activity?: ApiActivity;
}

export interface ApiActivitySnapshot {
  blocking: number;
  background: number;
}

type ActivityListener = () => void;

const activityListeners = new Set<ActivityListener>();
let blockingRequests = 0;
let backgroundRequests = 0;
let activitySnapshot: ApiActivitySnapshot = { blocking: 0, background: 0 };

function publishActivity(): void {
  activitySnapshot = { blocking: blockingRequests, background: backgroundRequests };
  activityListeners.forEach(listener => listener());
}

function beginActivity(activity: ApiActivity): () => void {
  if (activity === 'silent') return () => undefined;
  if (activity === 'blocking') blockingRequests += 1;
  else backgroundRequests += 1;
  publishActivity();

  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    if (activity === 'blocking') blockingRequests = Math.max(0, blockingRequests - 1);
    else backgroundRequests = Math.max(0, backgroundRequests - 1);
    publishActivity();
  };
}

export function subscribeApiActivity(listener: ActivityListener): () => void {
  activityListeners.add(listener);
  return () => activityListeners.delete(listener);
}

export function getApiActivitySnapshot(): ApiActivitySnapshot {
  return activitySnapshot;
}

const SESSION_STORAGE_KEY = 'lattice_session_token';

// Capture a one-time session token delivered via the URL fragment (set by the
// OAuth callback redirect: "#session=<token>"), persist it for the tab, and
// strip it from the visible URL/history immediately.
function captureSessionTokenFromFragment(): void {
  if (!window.location.hash?.startsWith('#session=')) {
    return;
  }
  const token = decodeURIComponent(window.location.hash.slice('#session='.length));
  if (token) {
    sessionStorage.setItem(SESSION_STORAGE_KEY, token);
  }
  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState({}, '', url.toString());
}

captureSessionTokenFromFragment();

// clearSessionToken drops the locally-held session token (call on logout).
export function clearSessionToken(): void {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

// apiFetch issues a request against the backend, prefixing the API base URL.
// It is a drop-in for `fetch(`${API_URL}${path}`, init)` so callers retain full
// control over response and status-code handling.
export async function apiFetch(path: string, init?: RequestInit, options?: ApiFetchOptions): Promise<ApiResponse> {
  const method = (init?.method ?? 'GET').toUpperCase();
  // Reads generally own a local skeleton/loading state. Mutations block by
  // default because many need a server-issued id/revision before the UI can
  // safely continue. Optimistic callers explicitly opt into background mode.
  const activity = options?.activity ?? (method === 'GET' ? 'silent' : 'blocking');
  const finishActivity = beginActivity(activity);
  try {
    const base = new URL(API_URL, window.location.origin);
    const url = /^https?:\/\//i.test(path) ? new URL(path) : new URL(base.href.replace(/\/$/, '') + (path.startsWith('/') ? path : '/' + path));
    if (url.origin !== base.origin || (url.pathname !== '/api' && !url.pathname.startsWith('/api/'))) {
      throw new Error('Authenticated requests must target the configured API.');
    }
    const headers = new Headers(init?.headers);
    const token = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return await fetch(url.href, { ...init, headers, credentials: 'include' });
  } finally {
    finishActivity();
  }
}

// jsonInit builds a RequestInit for a JSON-body mutation (POST/PUT/…), removing
// the repeated Content-Type header + JSON.stringify boilerplate at call sites.
export function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}
